import { useEffect, useState } from "react"

import {
  createVoiceLevelStore,
  type VoiceLevelStore,
} from "./use-voice-audio-level"

// Hold the pulse at rest this long after assistant audio becomes audible. On
// browsers that DO report an inbound audioLevel, the first real level arrives
// within this window and latches `assistantMetered`, so the synthetic pulse is
// never asked to drive the visualization — no "starts big then snaps down to the
// real level" handoff. The session's first audible response doubles as the
// probe, and the latch persists for the rest of the session. The
// caller anchors `active` to audio in the playback buffer, not the
// `assistant_speaking` status, which flips at `response.created` — generation
// can run well ahead of sound, and the probe window must cover audible audio.
const SYNTHETIC_PULSE_DELAY_MS = 250
// Ease the pulse in from rest over this long so it grows into motion instead of
// popping straight to full amplitude on browsers that never meter.
const SYNTHETIC_PULSE_RAMP_MS = 250

/**
 * Time-driven 0..1 "speaking" amplitude for browsers that don't report an
 * inbound audioLevel in getStats for the assistant's remote stream. A few
 * detuned sines so the motion looks organic rather than a mechanical throb.
 *
 * Also what drives the mini-player gallery's simulated speaking: that page has
 * no session to meter, and this is the same fallback a real un-metered browser
 * would use, so the preview moves like the real thing.
 */
export function useVoiceSyntheticPulse(
  active: boolean
): VoiceLevelStore<number> {
  const [pulse] = useState(() => createVoiceLevelStore(0))

  useEffect(() => {
    if (!active || typeof window === "undefined") {
      return
    }
    let frame = 0
    const start = performance.now()
    const tick = () => {
      const elapsed = performance.now() - start
      // Rest during the grace window; metering-capable browsers latch and take
      // over before it ends, so they never see the synthetic pulse at all.
      if (elapsed < SYNTHETIC_PULSE_DELAY_MS) {
        frame = requestAnimationFrame(tick)
        return
      }
      const t = (elapsed - SYNTHETIC_PULSE_DELAY_MS) / 1000
      const wave =
        0.5 * Math.sin(t * 5.6) +
        0.3 * Math.sin(t * 9.1 + 1.3) +
        0.2 * Math.sin(t * 13.7 + 2.7)
      const ramp = Math.min((t * 1000) / SYNTHETIC_PULSE_RAMP_MS, 1)
      const next = Math.min(Math.max((wave + 1) / 2, 0), 1) * ramp
      if (Math.abs(next - pulse.get()) > 0.01) {
        pulse.set(next)
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frame)
      pulse.set(0)
    }
  }, [active, pulse])

  return pulse
}
